import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllLots } from '../../services/Lot/lotService';
import CreateLotModal from './CreateLotModal';
import EditLotModal from './EditLotModal';
import { formatDate } from '../Utils/utils';
import LotFilter from '../Filters/LotFilter';
import { FaEdit } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Lot = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [persons, setLots] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateLotModal, setShowCreateLotModal] = useState(false);
  const [showEditLotModal, setShowEditLotModal] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});

  const canView = useMemo(() => hasPermission('lotView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('lotCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('lotUpdate'), [hasPermission, permissions]);

  const fetchLots = useCallback(async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
    if (!canView) return;
    try {
      const data = await getAllLots(filters, currentPage - 1, currentPageSize);
      setLots(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [filterParams, page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchLots();
    }
  }, [page, pageSize, fetchLots, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateLotModal = () => {
    if (!checkPermission('lotCreate')) {
      return;
    }
    setShowCreateLotModal(true);
  };
  const handleCloseCreateLotModal = () => setShowCreateLotModal(false);

  const handleOpenEditLotModal = (lot) => {
    if (!checkPermission('lotUpdate')) {
      return;
    }
    setSelectedLot(lot);
    setShowEditLotModal(true);
  };
  
  const handleCloseEditLotModal = () => {
    setSelectedLot(null);
    setShowEditLotModal(false);
  };
  
  const handleFilter = (filters) => {
    if (!canView) return;
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);

    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      fetchLots(filters, 1, pageSize);
      previousFilterParams.current = filters;
    }
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setFilterParams({});
    setPage(1);
    fetchLots({}, 1, pageSize);
    previousFilterParams.current = {};
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Lotes</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Lotes</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateLotModal}>
              Cadastrar Lote
            </Button>
          )}
        </Col>
      </Row>
      {canView && <LotFilter onFilter={handleFilter} onClear={handleClearFilter} />}
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Valor do Crédito</th>
              <th>Data do Contrato</th>
              <th>Cliente</th>
              <th>Produto</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {persons.map((lot) => (
              <tr key={lot.id}>
                <td>{lot.description}</td>
                <td>{lot.creditValue.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}</td>
                <td>{formatDate(lot.contractAt)}</td>
                <td>{lot.person !== null ? lot.person.name : ''}</td>
                <td>{lot.product !== null ? lot.product.description : ''}</td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditLotModal(lot)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateLotModal
        show={showCreateLotModal}
        handleClose={handleCloseCreateLotModal}
        refreshLots={fetchLots}
      />

      <EditLotModal
        show={showEditLotModal}
        handleClose={handleCloseEditLotModal}
        lot={selectedLot}
        refreshLots={fetchLots}
      />
    </Container>
  );
};

export default Lot;